import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

export default function DevDashboard() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [resettingIp, setResettingIp] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'superadmin') navigate('/dashboard');
  }, [authLoading, user, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/dev-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.error || 'Failed to load dev dashboard');
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load dev dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user?.role === 'superadmin') fetchData();
  }, [user, fetchData]);

  const handleResetRateLimit = async (ip) => {
    setResettingIp(ip);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/dev-dashboard-reset-rate-limit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ip }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.error || 'Failed to reset');
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to reset rate limit');
    } finally {
      setResettingIp(null);
    }
  };

  const handleConfirm = async (orderId) => {
    setProcessingId(orderId);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/packages/confirm-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orderId }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.message || result.error || 'Failed to confirm order');
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to confirm order');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (orderId) => {
    const reason = window.prompt(tt('Reason for rejection:', '拒绝原因：'), '');
    if (reason === null) return;
    setProcessingId(orderId);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/packages/reject-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orderId, reason: reason || null }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.message || result.error || 'Failed to reject order');
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to reject order');
    } finally {
      setProcessingId(null);
    }
  };

  if (!user || user.role !== 'superadmin') return null;

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cardStyle = { backgroundColor: '#0d2818', borderColor: '#c9a961' };
  const rowStyle = { backgroundColor: '#152214', borderColor: '#3d5a4a' };

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-headline-lg font-bold" style={{ color: '#c9a961' }}>
            {tt('Developer Dashboard', '开发者仪表板')}
          </h1>
          <button
            onClick={fetchData}
            className="px-4 py-2 text-body-sm font-medium rounded border"
            style={{ color: '#c9a961', borderColor: '#c9a961' }}
          >
            {tt('Refresh', '刷新')}
          </button>
        </div>
        <p className="text-body-xs mb-6" style={{ color: '#999999' }}>
          {tt(
            'Operational visibility for the Signup Wizard / Stripe integration: signup rate-limit state, webhook delivery health, and card payments awaiting manual confirmation.',
            '注册向导 / Stripe 集成的运行状态：注册频率限制状态、Webhook 送达情况，以及等待人工确认的银行卡付款。'
          )}
        </p>

        {error && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
            {error}
          </div>
        )}

        {loading || !data ? (
          <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
        ) : (
          <>
            {/* Pending Signup Wizard confirmations */}
            <div className="border rounded-lg p-6 mb-8" style={cardStyle}>
              <h3 className="text-headline-sm font-bold mb-1" style={{ color: '#c9a961' }}>
                {tt('Signup Wizard — Awaiting Payment Confirmation', '注册向导 — 等待付款确认')}
              </h3>
              <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
                {tt(
                  'Stripe already collected these payments; the package activates only after you confirm here (or in Reports > All Transactions).',
                  'Stripe 已收取这些款项；只有在此处（或报告 > 全部交易中）确认后，套餐才会激活。'
                )}
              </p>
              {data.pendingSignupWizardOrders.length === 0 ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('None pending.', '暂无待确认订单。')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                    <thead>
                      <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                        <th className="p-2">{tt('Member', '会员')}</th>
                        <th className="p-2">{tt('Package', '套餐')}</th>
                        <th className="p-2">{tt('Amount', '金额')}</th>
                        <th className="p-2">{tt('Payment Ref', '付款参考')}</th>
                        <th className="p-2">{tt('Date', '日期')}</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pendingSignupWizardOrders.map((o) => (
                        <tr key={o.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                          <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(o.member)}</td>
                          <td className="p-2">{o.package_name} ({o.package_code})</td>
                          <td className="p-2">{fmt(o.amount)}</td>
                          <td className="p-2 font-mono text-body-xs" style={{ color: '#999999' }}>{o.stripe_payment_intent_id || '—'}</td>
                          <td className="p-2">{new Date(o.created_at).toLocaleString()}</td>
                          <td className="p-2">
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => handleConfirm(o.id)}
                                disabled={processingId === o.id}
                                className="px-2 py-1 rounded text-body-xs font-medium disabled:opacity-50"
                                style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                              >
                                {tt('Confirm', '确认')}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleReject(o.id)}
                                disabled={processingId === o.id}
                                className="px-2 py-1 rounded text-body-xs font-medium border disabled:opacity-50"
                                style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                              >
                                {tt('Reject', '拒绝')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Signup rate limits */}
            <div className="border rounded-lg p-6 mb-8" style={cardStyle}>
              <h3 className="text-headline-sm font-bold mb-1" style={{ color: '#c9a961' }}>
                {tt('Signup Rate Limits', '注册频率限制')}
              </h3>
              <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
                {tt(
                  'Applies to /api/auth/email/signup, used by public /signup and both Signup Wizard card-payment paths. Only IPs with activity in the last hour are shown.',
                  '适用于 /api/auth/email/signup，被公开的 /signup 及两种注册向导银行卡付款流程使用。仅显示过去一小时内有活动的 IP。'
                )}
              </p>
              {data.rateLimits.length === 0 ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No recent activity.', '暂无近期活动。')}</p>
              ) : (
                <div className="space-y-2">
                  {data.rateLimits.map((r) => (
                    <div key={r.ip} className="flex items-center justify-between p-3 rounded border" style={rowStyle}>
                      <div>
                        <span className="font-mono" style={{ color: '#c9a961' }}>{r.ip}</span>
                        <span className="text-body-xs ml-3" style={{ color: r.allowed ? '#b8a68f' : '#e05252' }}>
                          {r.recent_requests} / {r.limit} {tt('requests this hour', '次请求（本小时内）')}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleResetRateLimit(r.ip)}
                        disabled={resettingIp === r.ip}
                        className="px-3 py-1 rounded text-body-xs font-medium border disabled:opacity-50"
                        style={{ backgroundColor: 'transparent', color: '#b8a68f', borderColor: '#3d5a4a' }}
                      >
                        {tt('Reset', '重置')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stripe webhook health */}
            <div className="border rounded-lg p-6" style={cardStyle}>
              <h3 className="text-headline-sm font-bold mb-1" style={{ color: '#c9a961' }}>
                {tt('Stripe Webhook Events (Recent)', 'Stripe Webhook 事件（最近）')}
              </h3>
              <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
                {tt(
                  'A row only appears here once our server has verified and processed an event from Stripe. If a payment succeeded but nothing shows up here, the webhook isn\'t reaching this server.',
                  '只有在本服务器验证并处理了来自 Stripe 的事件后，才会在此显示记录。如果付款已成功但此处没有记录，说明 Webhook 未能送达此服务器。'
                )}
              </p>
              {data.webhookEvents.length === 0 ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No webhook events recorded yet.', '尚无 Webhook 事件记录。')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                    <thead>
                      <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                        <th className="p-2">{tt('Event Type', '事件类型')}</th>
                        <th className="p-2">{tt('Order ID', '订单 ID')}</th>
                        <th className="p-2">{tt('Top-up ID', '充值 ID')}</th>
                        <th className="p-2">{tt('Received At', '接收时间')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.webhookEvents.map((e) => (
                        <tr key={e.stripe_event_id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                          <td className="p-2">{e.event_type}</td>
                          <td className="p-2 font-mono text-body-xs">{e.order_id || '—'}</td>
                          <td className="p-2 font-mono text-body-xs">{e.topup_id || '—'}</td>
                          <td className="p-2">{new Date(e.received_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
